// The checkout pages' (/upgrade, /gems) check that the backend is up before a
// buyer starts paying.
//
// It used to send buyers to /maintenance on ANY non-2xx, including a 429 and
// the 503 {"retry": true} with Retry-After that the backend answers for a few
// seconds while a store switches to PostgreSQL. Now (S19, 6 October 2026):
//   - 503, 502, 504 or a network failure: wait (Retry-After for a 503, clamped)
//     and ask again, at most MAX_ATTEMPTS times; still failing is an outage.
//   - 429: ask once more; a second 429 is not an outage, so the buyer stays.
//   - any other answer: as before (2xx is up, the rest is an outage).

export const MAX_ATTEMPTS = 3
const TRANSIENT = new Set([502, 503, 504])
const MIN_WAIT_MS = 1000
const MAX_WAIT_MS = 10000

// Retry-After in seconds (the backend never sends the date form), clamped so a
// buyer never stares at a spinner for long.
export const retryAfterMs = (response) => {
  const seconds = Number(response?.headers?.get?.('Retry-After'))
  if (!Number.isFinite(seconds) || seconds <= 0) return MIN_WAIT_MS * 2
  return Math.min(MAX_WAIT_MS, Math.max(MIN_WAIT_MS, seconds * 1000))
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Resolves to { state: 'up', response } | { state: 'busy' } | { state: 'outage' }.
// Only 'outage' should send a buyer to /maintenance.
export async function checkServerStatus({
  url,
  token,
  fetchImpl = fetch,
  wait = sleep,
}) {
  let rateLimitedOnce = false
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
    const last = attempt === MAX_ATTEMPTS
    let response
    try {
      response = await fetchImpl(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      })
    } catch {
      if (last) return { state: 'outage' }
      await wait(MIN_WAIT_MS)
      continue
    }
    if (response.ok) return { state: 'up', response }
    if (response.status === 429) {
      if (rateLimitedOnce || last) return { state: 'busy' }
      rateLimitedOnce = true
      await wait(retryAfterMs(response))
      continue
    }
    if (TRANSIENT.has(response.status) && !last) {
      await wait(response.status === 503 ? retryAfterMs(response) : MIN_WAIT_MS)
      continue
    }
    return { state: 'outage' }
  }
  return { state: 'outage' }
}
